import { query } from '@nftx/utils';
import { queryGraph } from '@nftx/query';
export { createGraphQuery as createQuery, gql } from '@nftx/query';

/** Addresses must be lowercased when sent to the subgraph, so we'll just automatically do this for any address variables */
const normalizeIfAddress = t => {
  if (typeof t === 'string' && t.toLowerCase().startsWith('0x')) {
    return t.toLowerCase();
  }
  return t;
};

const isDefined = x => x != null;
const buildPrimative = value => {
  switch (typeof value) {
    case 'string':
      // If the value is an address, we want to normalize it
      // Often we hit issues where the subgraph has lowercase addresses but we're passing uppercase values in
      // resulting in 0 results
      value = normalizeIfAddress(value);
      break;
    case 'bigint':
      // JSON.stringify doesn't work on bigints so we need to handle them separately
      return value.toString();
  }

  // Stringify the primitive value
  return JSON.stringify(value);
};
const isObjectEmpty = obj => {
  return Object.values(obj).every(v => {
    if (v == null) {
      return true;
    }
    if (isOperatorObject(v)) {
      return isObjectEmpty(v);
    }
    return false;
  });
};
const buildNestedFilter = (key, value) => {
  // Handle nested queries
  if (key.endsWith('_') === false) {
    key = `${key}_`;
  }

  // We need to make sure this isn't an empty object
  const isEmpty = isObjectEmpty(value);
  if (isEmpty) {
    return null;
  }
  const valueStr = buildObject(value);
  return [key, valueStr];
};
const buildArray = (key, arr) => {
  if (key.endsWith('_in') === false) {
    key = `${key}_in`;
  }

  // return [key, JSON.stringify(arr.map((el) => buildPrimative(el)))];
  return [key, `[${arr.map(el => buildPrimative(el))}]`];
};
const isOperatorObject = obj => {
  if (obj == null || typeof obj !== 'object') {
    return false;
  }
  const operators = ['is', 'isNot', 'gt', 'gte', 'lt', 'lte', 'in'];
  return Object.keys(obj).every(key => operators.includes(key));
};
const buildOperatorObject = (key, obj) => {
  return Object.entries(obj).map(([operator, value]) => {
    if (value == null) {
      return null;
    }
    switch (operator) {
      case 'is':
        return [key, buildPrimative(value)];
      case 'isNot':
        return [`${key}_ne`, buildPrimative(value)];
      default:
        return [`${key}_${operator}`, buildPrimative(value)];
    }
  }).filter(isDefined);
};
const buildKeyValuePair = (key, value) => {
  // Strip out null
  if (value == null) {
    return null;
  }
  if (Array.isArray(value)) {
    return [buildArray(key, value)];
  }
  if (typeof value === 'object') {
    if (isOperatorObject(value)) {
      return buildOperatorObject(key, value);
    } else {
      const pair = buildNestedFilter(key, value);
      return pair && [pair];
    }
  }
  return [[key, buildPrimative(value)]];
};
const buildObject = obj => {
  const keyValues = Object.entries(obj).reduce((acc, [key, value]) => {
    const pair = buildKeyValuePair(key, value);
    if (!pair) {
      return acc;
    }
    return [...acc, ...pair];
  }, []);
  return ['{', keyValues.map(pair => pair.join(': ')).join(', '), '}'].join(' ');
};

/**
 * Helper method that builds a subgraph where clause
 * Takes an object of key/values, removes null items, and returns the stringified result.
 * If you pass any addresses (i.e. 0x0000) it will lowercase them.
 * If you pass in a deeply-nested object it will translate it into a nested query
 */
const buildWhere = obj => {
  return buildObject(obj);
};
var buildWhere$1 = buildWhere;

const formatQuery = ({
  query,
  requiredBlock
}) => {
  if (typeof query !== 'string') {
    query = query.toString();
  }
  if (requiredBlock) {
    query = query.replace(/\{/, `{\n  _meta { block { number } }`);
  }
  return query;
};
const queryWhileSubgraphBehind = async ({
  query: query$1,
  requiredBlock,
  url,
  fetch,
  variables
}) => {
  // If we're given a required block, we want to add it to the query string, and check it
  // if the subgraph is behind, we'll wait and try again (up to 3 times)
  let blockChecks = 0;
  do {
    const data = await queryGraph({
      url,
      query: query$1,
      sendQuery: query,
      fetch,
      variables
    });

    // If we're not checking for a specific block, we can stop here
    if (!requiredBlock) {
      return data;
    }
    // If the subgraph is up to date, we can stop here
    if (Number(data?._meta?.block?.number) >= requiredBlock) {
      delete data._meta;
      return data;
    }
    // If we've tried 3 times and the subgraph is still behind, throw an error
    if (blockChecks >= 2) {
      throw new Error(`Subgraph at ${url} is not up to date. Expected block ${requiredBlock}, got ${data?._meta?.block?.number}`);
    }
    // Wait 5s and try again
    blockChecks += 1;
    await new Promise(res => setTimeout(res, 5000));
  } while (blockChecks < 3);
  throw new Error(`Subgraph at ${url} is not up to date`);
};

/** Sends a request to the subgraph
 * Uses the fetch api under the hood so if running in node you'll need to polyfill global.fetch
 */

async function querySubgraph({
  url: baseUrl,
  query,
  variables,
  fetch,
  requiredBlock
}) {
  return await queryWhileSubgraphBehind({
    query: formatQuery({
      query,
      requiredBlock
    }),
    requiredBlock,
    url: baseUrl,
    fetch,
    variables
  });
}

export { buildWhere$1 as buildWhere, querySubgraph };
